var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Vector3 } from '@skand/math';
import { spatialReferenceSystems } from './DataTypes';
import proj4 from 'proj4';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import initGdalJsNode from 'gdal3.js/node';
import initGdalJsBrowser from 'gdal3.js';
const isNode = Object.prototype.toString.call(typeof process !== 'undefined' ? process : 0) === '[object process]'; // https://github.com/iliakan/detect-node/blob/master/index.js
/**
 * Set this from the browser side to point to the public root folder.
 */
export const GLOBAL_CONFIG = {
    staticRoot: '',
};
/**
 * Global GDAL instance. Do not call this until the static root configuration is set.
 */
let GDAL_PROMISE;
function getGDAL() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!GDAL_PROMISE) {
            if (isNode)
                GDAL_PROMISE = initGdalJsNode();
            else {
                GDAL_PROMISE = initGdalJsBrowser({
                    paths: {
                        wasm: `${GLOBAL_CONFIG.staticRoot}/gdal3WebAssembly.wasm`,
                        data: `${GLOBAL_CONFIG.staticRoot}/gdal3WebAssembly.data`,
                        js: `${GLOBAL_CONFIG.staticRoot}/gdal3.js`,
                    },
                });
            }
        }
        return yield GDAL_PROMISE;
    });
}
/**
 * A note on GDA94/GDA2020 <-> WGS84 conversion
 *
 * Australia's datums have shifted thanks to continental drift. GDA2020 accounts for this drift,
 * and so coordinates are ~1.5 meters north-east from GDA94.
 *
 * Converting GDA94/GDA2020 directly to WGS84 results in them having the same output coordinates, as it
 * does not account for the drift.
 *
 * Previously, we attempted to approximate the drift by hard coding a 7-parameter helmert transform. However,
 * we found that this was incorrect and changed it to use the NTv2 grid provided by GDAL. To convert GDA2020
 * to WGS84, we must perform a two-step transform (GDA2020 -> GDA94 -> WGS84).
 *
 * See https://www.spatial.nsw.gov.au/__data/assets/pdf_file/0008/224396/WGS84_and_Australias_misaligned_web-maps_Information_Sheet.pdf
 *
 * Some third-party programs don't comply with this two-step transformation, so we support the legacy 7-parameter
 * transform as well.
 */
// GDA2020 / GDA94 projection mapping
const GDA_MAPPING = {
    'EPSG:7848': 'EPSG:28348',
    'EPSG:7849': 'EPSG:28349',
    'EPSG:7850': 'EPSG:28350',
    'EPSG:7851': 'EPSG:28351',
    'EPSG:7852': 'EPSG:28352',
    'EPSG:7853': 'EPSG:28353',
    'EPSG:7854': 'EPSG:28354',
    'EPSG:7855': 'EPSG:28355',
    'EPSG:7856': 'EPSG:28356',
    'EPSG:7857': 'EPSG:28357',
    'EPSG:7858': 'EPSG:28358',
};
function toProjectedRaw(points_1, epsg_1) {
    return __awaiter(this, arguments, void 0, function* (points, epsg, gda7param = false) {
        const ecef = 'EPSG:4978';
        const GDAL = yield getGDAL();
        if ((Object.keys(GDA_MAPPING).includes(epsg) ||
            Object.values(GDA_MAPPING).includes(epsg)) &&
            gda7param) {
            proj4.defs(epsg, spatialReferenceSystems[epsg].proj4);
            proj4.defs(ecef, spatialReferenceSystems[ecef].proj4);
            return points.map((point) => {
                const { x, y, z } = proj4(ecef, epsg).forward(point);
                return new Vector3(x, y, z);
            });
        }
        // Two-step transform
        const p0 = points.map((point) => [point.x, point.y, point.z]);
        if (Object.keys(GDA_MAPPING).includes(epsg)) {
            const p1 = yield GDAL.gdaltransform(p0, [
                '-s_srs',
                ecef,
                '-t_srs',
                GDA_MAPPING[epsg],
            ]);
            const p2 = yield GDAL.gdaltransform(p1, [
                '-s_srs',
                GDA_MAPPING[epsg],
                '-t_srs',
                epsg,
            ]);
            return p2.map(([x, y, z]) => new Vector3(x, y, z));
        }
        else {
            const p1 = yield GDAL.gdaltransform(p0, ['-s_srs', ecef, '-t_srs', epsg]);
            return p1.map(([x, y, z]) => new Vector3(x, y, z));
        }
    });
}
function toECEFRaw(points_1, epsg_1) {
    return __awaiter(this, arguments, void 0, function* (points, epsg, gda7param = false) {
        const ecef = 'EPSG:4978';
        const GDAL = yield getGDAL();
        if ((Object.keys(GDA_MAPPING).includes(epsg) ||
            Object.values(GDA_MAPPING).includes(epsg)) &&
            gda7param) {
            proj4.defs(epsg, spatialReferenceSystems[epsg].proj4);
            proj4.defs(ecef, spatialReferenceSystems[ecef].proj4);
            return points.map((point) => {
                const { x, y, z } = proj4(epsg, ecef).forward(point);
                return new Vector3(x, y, z);
            });
        }
        // Two-step transform
        const p0 = points.map((point) => [point.x, point.y, point.z]);
        if (Object.keys(GDA_MAPPING).includes(epsg)) {
            const p1 = yield GDAL.gdaltransform(p0, [
                '-s_srs',
                epsg,
                '-t_srs',
                GDA_MAPPING[epsg],
            ]);
            const p2 = yield GDAL.gdaltransform(p1, [
                '-s_srs',
                GDA_MAPPING[epsg],
                '-t_srs',
                ecef,
            ]);
            return p2.map(([x, y, z]) => new Vector3(x, y, z));
        }
        else {
            const p1 = yield GDAL.gdaltransform(p0, ['-s_srs', epsg, '-t_srs', ecef]);
            return p1.map(([x, y, z]) => new Vector3(x, y, z));
        }
    });
}
/**
 * Sanitize path strings with backslashes.
 *
 * @param path
 * @returns
 */
export function slash(path) {
    const isExtendedLengthPath = path.startsWith('\\\\?\\');
    if (isExtendedLengthPath) {
        return path;
    }
    return path.replace(/\\/g, '/');
}
/**
 * Batch an array of objects.
 *
 * @param arr
 * @param batchSize
 * @returns
 */
export function batch(arr, batchSize) {
    const batches = [];
    let batch = [];
    for (let i = 0; i < arr.length; i++) {
        batch.push(arr[i]);
        if ((i + 1) % batchSize === 0) {
            batches.push(batch);
            batch = [];
        }
    }
    batches.push(batch);
    return batches;
}
/**
 * Converts a set of points in ECEF to a projected coordinate system.
 *
 * @param points
 * @param epsg
 * @param gda7param
 * @param batchSize
 * @returns
 */
export function toProjected(points_1, epsg_1) {
    return __awaiter(this, arguments, void 0, function* (points, epsg, gda7param = false, batchSize = 1000) {
        const batches = batch(points, batchSize);
        const transformed = [];
        for (const batch of batches) {
            const points = yield toProjectedRaw(batch, epsg, gda7param);
            for (const point of points) {
                transformed.push(point);
            }
        }
        return transformed;
    });
}
/**
 * Converts a set of points in a projected coordinate system to ECEF.
 *
 * @param points
 * @param epsg
 * @param gda7param
 * @param batchSize
 * @returns
 */
export function toECEF(points_1, epsg_1) {
    return __awaiter(this, arguments, void 0, function* (points, epsg, gda7param = false, batchSize = 1000) {
        const batches = batch(points, batchSize);
        const transformed = [];
        for (const batch of batches) {
            const points = yield toECEFRaw(batch, epsg, gda7param);
            for (const point of points) {
                transformed.push(point);
            }
        }
        return transformed;
    });
}
